import React from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Typography,
} from "@mui/material";
import { CTAButton } from "../home/blocks/odyssee/OdysseeCard";
import { useSelector } from "react-redux";
import { getUser } from "../../store/reducers/authSlice";
import { calculateProductPrice } from "../basket/BasketProductCard";
import { CloseRounded, ShoppingBagOutlined } from "@mui/icons-material";
import backgroundImage from "../../resources/images/png/events/img.png";

const SubscribeButton = ({ subscriptionKey = undefined, isSubscribed, promo }) => {
	const indexesInUrl = React.useMemo(() => {
		switch (subscriptionKey) {
			case "digital":
				return [1];
			case "tv":
				return [2];
			case "mag":
				return [3];
			default:
				return [2, 3];
		}
	}, [subscriptionKey]);

	return (
		<>
			<Typography
				sx={{
					color: (theme) => theme.palette.yellow,
					textDecoration: "none",
					marginBottom: (theme) => theme.spacing(1),
				}}
			>
				{`Soit une réduction de ${(promo / 100).toFixed(2).replace(".", ",")}€`}
			</Typography>
			<a
				href={
					!isSubscribed
						? `https://abonnements.inexplore.com/step/subscription?selectedAbos=[${indexesInUrl}]`
						: "#"
				}
				aria-disabled={isSubscribed}
			>
				<CTAButton
					text={isSubscribed ? "Déjà abonné" : "Sélectionner cet abonnement"}
					textWithGradient={false}
					disableBackground={true}
					disabled={isSubscribed}
					sx={{
						flexDirection: "row-reverse",
						color: (theme) => (!isSubscribed ? theme.palette.secondary.main : theme.palette.grey[500]),
						backgroundColor: (theme) => (!isSubscribed ? theme.palette.yellow : theme.palette.grey[500]),
						"& > p": {
							backgroundColor: "transparent",
						},
					}}
				/>
			</a>
		</>
	);
};

const EventReductionsDetailDialog = ({ open, onClose, product }) => {
	const user = useSelector(getUser);
	const [totalPrice, totalPromo, totalPriceBeforePromo] = React.useMemo(() => {
		if (!product) return [0, 0, 0];
		return calculateProductPrice(product, user);
	}, [product, user]);

	const maxPromo = React.useMemo(() => {
		const idx = ["reduct_tv", "reduct_mag"];
		return idx.reduce((acc, key) => acc + (product?.[key] ?? 0), 0);
	}, [product]);

	const rows = React.useMemo(() => {
		let rows = [];
		const nowverif = new Date().getTime() / 1000;

		if (!user || (!(user["digital"] > nowverif) && !(user["tv"] > nowverif) && !(user["mag"] > nowverif))) {
			rows = [
				{
					name: "Inexploré TV + digital + magazine",
					subKey: undefined,
					key: undefined,
					price: 12.9,
					promo: maxPromo,
					mostInteresting: true,
				},
				{
					name: "Inexploré TV + digital",
					subKey: "tv",
					key: "reduct_tv",
					price: 9.9,
					promo: product?.reduct_tv,
				},
				{
					name: "Inexploré Magazine",
					subKey: "mag",
					key: "reduct_mag",
					price: 3.0,
					promo: product?.reduct_mag,
				},
				{
					name: "Inexploré digital",
					subKey: "digital",
					key: "reduct_digit",
					price: 2.9,
					promo: product?.reduct_digit,
				},
			];
		} else {
			if (!(user["tv"] > nowverif)) {
				if (!(user["mag"] > nowverif)) {
					rows.push({
						name: "Inexploré TV + digital + magazine",
						subKey: undefined,
						key: undefined,
						price: 12.9,
						promo: maxPromo,
						mostInteresting: true,
						doCalculation: true,
					});
					rows.push({
						name: "Inexploré TV + digital",
						subKey: "tv",
						key: "reduct_tv",
						price: 9.9,
						promo: product?.reduct_tv,
					});
					rows.push({
						name: "Inexploré magazine",
						subKey: "mag",
						key: "reduct_mag",
						price: 3.0,
						promo:
							user["digital"] > nowverif
								? product?.reduct_digit + product?.reduct_mag
								: product?.reduct_mag,
					});
				} else {
					rows.push({
						name: "Inexploré TV + digital + magazine",
						subKey: undefined,
						key: undefined,
						price: 12.9,
						promo: maxPromo,
						mostInteresting: true,
						doCalculation: true,
					});
					rows.push({
						name: "Inexploré TV + digital",
						subKey: "tv",
						key: "reduct_tv",
						price: 9.9,
						promo: product?.reduct_tv + product?.reduct_mag,
					});
					if (!(user["digital"] > nowverif)) {
						rows.push({
							name: "Inexploré digital",
							subKey: "digital",
							key: "reduct_digit",
							price: 2.9,
							promo: product?.reduct_digit + product?.reduct_mag,
						});
					}
				}
			} else {
				if (!(user["mag"] > nowverif)) {
					rows.push({
						name: "Inexploré TV + digital + magazine",
						subKey: undefined,
						key: undefined,
						price: 12.9,
						promo: maxPromo,
						mostInteresting: true,
						doCalculation: true,
					});
				}
			}
		}
		return rows;
	}, [user, maxPromo]);

	const [subscriptionName, subscriptionPrice] = React.useMemo(() => {
		const nowverif = new Date().getTime() / 1000;
		let subscriptionName = "";
		let subscriptionPrice = 0;
		if (user) {
			if (user["tv"] > nowverif) {
				subscriptionName += "TV + digital";
				subscriptionPrice += 9.9;
			} else {
				if (user["digital"] > nowverif) {
					subscriptionName += "digital";
					subscriptionPrice += 2.9;
				}
			}
			if (user["mag"] > nowverif) {
				subscriptionName += `${subscriptionName === "" ? "" : "+ "}magazine`;
				subscriptionPrice += 3;
			}
		}
		return [subscriptionName, subscriptionPrice];
	}, [user]);

	return (
		<Dialog
			open={open}
			maxWidth="lg"
			onClose={() => {
				onClose(false);
			}}
			sx={{}}
		>
			<IconButton
				sx={{
					position: "absolute",
					top: "20px",
					right: "20px",
				}}
				onClick={() => {
					onClose(false);
				}}
			>
				<CloseRounded />
			</IconButton>
			<DialogTitle
				component={"h3"}
				sx={{
					width: "100%",
					textAlign: "center",
					fontSize: 30,
				}}
			>
				Bénéficiez de réductions grâce à nos abonnements !
			</DialogTitle>
			<DialogContent>
				<Table sx={{ minWidth: 650 }} aria-label="simple table">
					<TableBody
						sx={{
							fontFamily: "Branding Medium",
						}}
					>
						<TableRow
							key={"noPromo"}
							sx={{
								"&:last-child td, &:last-child th": {
									border: 0,
								},
							}}
						>
							<TableCell
								component="th"
								scope="row"
								sx={{
									fontFamily: "Branding SemiBold",
								}}
							>
								{subscriptionName !== "" && (
									<Typography>{`Avec vos abonnements à ${subscriptionPrice.toFixed(2).replace(".", ",")}€ par mois`}</Typography>
								)}
								<Typography>
									{subscriptionName === ""
										? `Aucun abonnement inexploré`
										: `Inexploré ${subscriptionName}`}
								</Typography>
							</TableCell>
							<TableCell
								align="center"
								sx={{
									fontFamily: "Branding Bold",
								}}
							>
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
									}}
								>
									<Box
										sx={{
											borderRadius: "10px",
											boxShadow: (theme) => theme.shadows[5],
											width: "100px",
											height: "100px",
											overflow: "hidden",
											marginRight: 2,
										}}
									>
										<img
											src={
												product
													? `https://medias.inrees.com/img/produit/moyen_${product.id}.jpg`
													: backgroundImage
											}
											alt={"eventImage"}
											style={{
												objectFit: "contain",
												width: "100%",
											}}
										/>
									</Box>
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											alignItems: "flex-start",
										}}
									>
										<Typography
											sx={{
												color: (theme) => theme.palette.grey[700],
											}}
										>
											{product.titre}
										</Typography>
										<Box
											sx={{
												marginTop: 1,
												display: "flex",
												flexDirection: "row",
												alignItems: "flex-end",
												flexWrap: "nowrap",
											}}
										>
											<Typography
												sx={{
													color: (theme) => theme.palette.primary.main,
													fontFamily: "Branding Bold",
													fontSize: 24,
												}}
											>
												{`${totalPrice.toFixed(2).replace(".", ",")}€`}
											</Typography>
											{totalPrice !== totalPriceBeforePromo && (
												<>
													<Typography sx={{ marginLeft: 1, marginBottom: 0.5 }}>
														{`au lieu de `}
													</Typography>
													<Typography
														sx={{
															marginLeft: 1,
															textDecoration: "line-through",
															marginBottom: 0.5,
														}}
													>
														{`${totalPriceBeforePromo.toFixed(2).replace(".", ",")}€`}
													</Typography>
												</>
											)}
										</Box>
									</Box>
								</Box>
							</TableCell>
							<TableCell align="center">
								<Typography
									sx={{
										color: "rgb(99, 184, 229)",
										textDecoration: "none",
										marginBottom: (theme) => theme.spacing(1),
									}}
								>
									{`Soit une réduction de ${totalPromo.toFixed(2).replace(".", ",")}€`}
								</Typography>
								<a href={product ? `/panier?id=${product.id}&quantity=${1}` : "#"}>
									<CTAButton
										variant={"texticon"}
										icon={
											<ShoppingBagOutlined
												sx={{
													fontSize: "26px",
													color: (theme) =>
														product
															? theme.palette.secondary.main
															: theme.palette.grey[300],
												}}
											/>
										}
										textWithGradient={false}
										disableBackground={true}
										text={"Ajouter au panier"}
										disabled={!product}
										sx={{
											padding: 3,
											marginRight: 2,
											flexDirection: "row-reverse",
											color: (theme) => theme.palette.secondary.main,
											backgroundColor: (theme) =>
												product ? "rgb(99, 184, 229)" : theme.palette.grey[500],
											"& > p": {
												backgroundColor: "transparent",
												color: (theme) => (product ? undefined : theme.palette.grey[300]),
											},
										}}
									/>
								</a>
							</TableCell>
						</TableRow>
						{rows.map((row) => {
							const prixAfterPromo = product.prix - (row.key ? row.promo : maxPromo);
							//const promo = row.key ? product[row.key] : maxPromo;
							const promo = row.key ? row.promo : maxPromo;

							const now = new Date().getTime() / 1000;
							const isSubscribed = !user ? false : user[row.subKey] > now;
							const isSubToAll = !user ? false : user["tv"] > now && user["mag"] > now;

							const rowPrice = row.doCalculation ? row.price - subscriptionPrice : row.price;

							return (
								<TableRow
									key={row.name}
									sx={{
										background: (theme) =>
											isSubscribed || isSubToAll ? theme.palette.grey[200] : undefined,
										"&:last-child td, &:last-child th": {
											border: 0,
										},
										border: (theme) =>
											row.mostInteresting ? `${theme.palette.yellow} solid 2px` : undefined,
										position: "relative",
										paddingTop: (theme) => theme.spacing(2),
										paddingBottom: (theme) => theme.spacing(2),
									}}
								>
									<TableCell
										component="th"
										scope="row"
										sx={{
											fontFamily: "Branding SemiBold",
										}}
									>
										<Typography>{row.name}</Typography>
										<Typography>{`Pour seulement ${rowPrice.toFixed(2).replace(".", ",")}€ en plus par mois`}</Typography>
									</TableCell>
									<TableCell align="center">
										<Box
											sx={{
												display: "flex",
												flexDirection: "row",
											}}
										>
											<Box
												sx={{
													borderRadius: "10px",
													boxShadow: (theme) => theme.shadows[5],
													width: "100px",
													height: "100px",
													overflow: "hidden",
													marginRight: 2,
												}}
											>
												<img
													src={
														product
															? `https://medias.inrees.com/img/produit/moyen_${product.id}.jpg`
															: backgroundImage
													}
													alt={"eventImage"}
													style={{
														objectFit: "contain",
														width: "100%",
													}}
												/>
											</Box>
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													justifyContent: "center",
													alignItems: "flex-start",
												}}
											>
												<Typography
													sx={{
														color: (theme) => theme.palette.grey[700],
													}}
												>
													{product.titre}
												</Typography>
												<Box
													sx={{
														marginTop: 1,
														display: "flex",
														flexDirection: "row",
														alignItems: "flex-end",
														flexWrap: "nowrap",
													}}
												>
													<Typography
														sx={{
															color: (theme) => theme.palette.primary.main,
															fontFamily: "Branding Bold",
															fontSize: 24,
														}}
													>
														{`${(prixAfterPromo / 100).toFixed(2).replace(".", ",")}€`}
													</Typography>
													<Typography sx={{ marginLeft: 1, marginBottom: 0.5 }}>
														{`au lieu de `}
													</Typography>
													<Typography
														sx={{
															marginLeft: 1,
															textDecoration: "line-through",
															marginBottom: 0.5,
														}}
													>
														{`${totalPriceBeforePromo.toFixed(2).replace(".", ",")}€`}
													</Typography>
												</Box>
											</Box>
										</Box>
									</TableCell>
									<TableCell align="center">
										<SubscribeButton
											subscriptionKey={row.subKey}
											isSubscribed={isSubscribed || isSubToAll}
											promo={promo}
										/>
									</TableCell>
									{row.mostInteresting && (
										<Typography
											variant={"caption"}
											sx={{
												position: "absolute",
												top: "-20px",
												left: "20px",
												background: (theme) => theme.palette.yellow,
												padding: (theme) => `${theme.spacing(1)} ${theme.spacing(2)}`,
												borderRadius: "20px",
											}}
										>
											Offre la plus intéressante
										</Typography>
									)}
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</DialogContent>
		</Dialog>
	);
};

export default EventReductionsDetailDialog;
